import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import { DateRangeFilter, inRange } from "@/components/DateRangeFilter";
import { Plus, X, Pencil, Trash2, Search, Save, Users, MapPin, UserCheck, Building2, CheckCircle2, Eye, Phone, Mail } from "lucide-react";
import { inputCls, Field } from "./Projects";
import { toast, Toaster } from "sonner";
import { useBranch } from "@/contexts/BranchContext";
import { BranchSelect } from "@/components/BranchSelect";
import { BranchFilter } from "@/components/BranchFilter";
import { BulkUploadButtons, BulkUploadResultBanner } from "@/components/BulkUploadControls";
import { useFormTab } from "@/hooks/useFormTab";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { PaginationControls } from "@/components/PaginationControls";
import { ActiveStatusCell, ActiveStatusFilter, matchActiveFilter } from "@/components/ActiveStatusCell";

const blank = () => ({
  client_code: "", name: "", branch_id: "", branch_name: "", contact_person: "", phone: "", email: "",
  address: "", gst_number: "", gst_verified: false, gst_verified_at: "",
  pan: "", cin: "",
  billing_address: "",
  shipping_addresses: [],
  pocs: [],
  opening_balance: 0,
  credit_days: null,
  reminder_cc: [],
  reminder_cc_4th: [],
  reminder_cc_7th: [],
  is_active: true,
});

// Jul-2026 — comma/semicolon-separated email list input (estimate reminder CC).
function EmailListInput({ label, value, onChange, testid, hint }) {
  const [raw, setRaw] = useState((value || []).join(", "));
  return (
    <Field label={label}>
      <input
        data-testid={testid}
        value={raw}
        onChange={e => {
          setRaw(e.target.value);
          onChange(e.target.value.split(/[,;]/).map(s => s.trim()).filter(Boolean));
        }}
        placeholder="email1@company.com, email2@company.com"
        className={inputCls}
      />
      {hint && <div className="text-[10px] text-slate-400 mt-1">{hint}</div>}
    </Field>
  );
}

export default function ClientsPage() {
  const [search, setSearch] = useState("");
  const [filterBranch, setFilterBranch] = useState("");
  const [filterActive, setFilterActive] = useState("active");
  const [createdRange, setCreatedRange] = useState({ from: "", to: "" });
  const { active: activeBranch } = useBranch();

  const params = useMemo(() => (filterBranch ? { branch_id: filterBranch } : {}), [filterBranch]);
  const { items: list, total, page, pageSize, loading, setPage, setPageSize, reload } = usePaginatedList("/clients", params);
  const load = reload;
  const openFormTab = useFormTab();

  const openNew = () => openFormTab({
    path: "/clients/new",
    label: "New Client",
    icon: Plus,
    parent: "/clients",
    onSaved: load,
    render: ({ close }) => <ClientForm record={blank()} activeBranch={activeBranch} onClose={close} />,
  });
  const openEdit = (c) => openFormTab({
    path: `/clients/edit/${c.id}`,
    label: `Edit · ${c.client_code || c.client_name || "Client"}`,
    icon: Pencil,
    parent: "/clients",
    onSaved: load,
    render: ({ close }) => <ClientForm record={c} activeBranch={activeBranch} onClose={close} />,
  });
  const openView = (c) => openFormTab({
    path: `/clients/view/${c.id}`,
    label: `View · ${c.client_code || c.client_name || "Client"}`,
    icon: Eye,
    parent: "/clients",
    render: ({ close }) => <ClientView record={c} onClose={close} onEdit={() => { close(); openEdit(c); }} />,
  });

  async function del(id, name) {
    if (!window.confirm(`Delete client "${name}"?`)) return;
    await api.delete(`/clients/${id}`);
    toast.success("Deleted"); load();
  }

  const filtered = list.filter(c => {
    if (filterBranch && c.branch_id !== filterBranch) return false;
    if (!matchActiveFilter(c, filterActive)) return false;
    if (!inRange(c.created_at, createdRange)) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (c.client_code || "").toLowerCase().includes(s) ||
           (c.name || "").toLowerCase().includes(s) ||
           (c.gst_number || "").toLowerCase().includes(s);
  });

  return (
    <div data-testid="clients-page">
      <Toaster richColors position="top-right" />
      <PageHeader eyebrow="Master" title="Clients"
        subtitle="Full client master with statutory codes, multiple Points of Contact, billing and multiple shipping addresses. The shipping addresses appear in Quote / Sales Invoice for selection."
        onRefresh={load}
        refreshing={loading}
        actions={
          <>
            <BulkUploadButtons master="clients" filename="clients_master_template.xlsx" onUploaded={load}
                               templateLabel="Client Template" uploadLabel="Bulk Upload Clients" />
            <div className="w-px h-7 bg-slate-300 self-center mx-1 hidden md:block" />
            <BulkUploadButtons master="client-shipping" filename="client_shipping_addresses_template.xlsx" onUploaded={load}
                               templateLabel="Shipping Template" uploadLabel="Bulk Upload Shipping" />
            <div className="w-px h-7 bg-slate-300 self-center mx-1 hidden md:block" />
            <ShipEmailBulkButtons onUploaded={load} />
            <button data-testid="add-client-btn" onClick={openNew}
                    className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
              <Plus size={14} /> Add Client
            </button>
          </>
        }
      />

      <BulkUploadResultBanner master="clients" />
      <BulkUploadResultBanner master="client-shipping" />

      <div className="relative mb-4 flex items-center gap-3 max-w-3xl">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
                 placeholder="Search by code, name, GST…"
                 className="w-full pl-9 pr-3 py-2.5 border border-slate-300 text-sm bg-white focus:border-[#F97316] focus:outline-none" />
        </div>
        <BranchFilter value={filterBranch} onChange={setFilterBranch} testid="clients-filter-branch" />
        <ActiveStatusFilter value={filterActive} onChange={setFilterActive} testid="clients-filter-active" />
        <DateRangeFilter value={createdRange} onChange={setCreatedRange} testidPrefix="clients" label="Created" />
      </div>

      <Card className="overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b-2 border-slate-200">
            <tr>{["Code", "Name", "GST", "PAN", "POCs", "Shipping Addrs", "Op. Balance", "Status", ""].map(h =>
              <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
            )}</tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="p-10 text-center text-slate-500 text-sm">No clients yet.</td></tr>
            )}
            {filtered.map(c => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="py-3 px-4 text-xs font-bold">{c.client_code}</td>
                <td className="py-3 px-4 text-sm">{c.name}</td>
                <td className="py-3 px-4 text-xs">
                  {c.gst_number || "—"} {c.gst_verified && <CheckCircle2 size={12} className="inline text-emerald-600 ml-1" />}
                </td>
                <td className="py-3 px-4 text-xs">{c.pan || "—"}</td>
                <td className="py-3 px-4 text-xs">{(c.pocs || []).length}</td>
                <td className="py-3 px-4 text-xs">{(c.shipping_addresses || []).length}</td>
                <td className="py-3 px-4 text-sm tabular">{formatINR(c.opening_balance || 0)}</td>
                <td className="py-3 px-4">
                  <ActiveStatusCell row={c} resource="clients" onChanged={load} />
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`view-client-${c.id}`} onClick={() => openView(c)} className="text-slate-500 hover:text-blue-600 p-1" title="View"><Eye size={14} /></button>
                    <button data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)} className="text-slate-500 hover:text-[#F97316] p-1" title="Edit"><Pencil size={14} /></button>
                    <button data-testid={`delete-client-${c.id}`} onClick={() => del(c.id, c.name)} className="text-slate-500 hover:text-red-600 p-1" title="Delete"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationControls page={page} pageSize={pageSize} total={total} loading={loading} onPage={setPage} onPageSize={setPageSize} />
      </Card>

    </div>
  );
}

function ClientForm({ record, activeBranch, onClose }) {
  const [form, setForm] = useState({ ...blank(), branch_id: activeBranch?.id || "", branch_name: activeBranch?.branch_name || "", ...record });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  // Collapse existing rows to "saved" view when starting to edit
  function collapseAll(arr) { return (arr || []).map(x => ({ ...x, _editing: false })); }

  // POC helpers
  function addPoc() {
    const collapsed = collapseAll(form.pocs);
    setForm({ ...form, pocs: [...collapsed, { name: "", designation: "", phone: "", email: "", notes: "", _editing: true }] });
  }
  function setPoc(i, k, v) {
    const arr = [...(form.pocs || [])]; arr[i] = { ...arr[i], [k]: v }; setForm({ ...form, pocs: arr });
  }
  function delPoc(i) {
    const arr = [...(form.pocs || [])]; arr.splice(i, 1); setForm({ ...form, pocs: arr });
  }
  function editPoc(i) {
    const arr = collapseAll(form.pocs); arr[i] = { ...arr[i], _editing: true }; setForm({ ...form, pocs: arr });
  }
  function savePoc(i) {
    const arr = [...(form.pocs || [])];
    if (!arr[i].name || !arr[i].name.trim()) { toast.error("POC name is required"); return false; }
    arr[i] = { ...arr[i], _editing: false }; setForm({ ...form, pocs: arr }); return true;
  }
  function savePocAndAdd(i) { if (savePoc(i)) addPoc(); }

  // Shipping addr helpers
  function addShip() {
    const collapsed = collapseAll(form.shipping_addresses);
    setForm({ ...form, shipping_addresses: [...collapsed, { label: "", code: "", format: "", address_line1: "", address_line2: "", city: "", state: "", pincode: "", contact_person: "", contact_phone: "", email: "", gst_number: form.gst_number || "", pan: form.pan || "", is_default: collapsed.length === 0, _editing: true }] });
  }
  function setShip(i, k, v) {
    const arr = [...(form.shipping_addresses || [])]; arr[i] = { ...arr[i], [k]: v }; setForm({ ...form, shipping_addresses: arr });
  }
  function delShip(i) {
    const arr = [...(form.shipping_addresses || [])]; arr.splice(i, 1); setForm({ ...form, shipping_addresses: arr });
  }
  function makeDefault(i) {
    const arr = (form.shipping_addresses || []).map((s, j) => ({ ...s, is_default: j === i }));
    setForm({ ...form, shipping_addresses: arr });
  }
  function editShip(i) {
    const arr = collapseAll(form.shipping_addresses); arr[i] = { ...arr[i], _editing: true }; setForm({ ...form, shipping_addresses: arr });
  }
  function saveShip(i) {
    const arr = [...(form.shipping_addresses || [])];
    if (!arr[i].label || !arr[i].label.trim()) { toast.error("Shipping address label is required"); return false; }
    if (!arr[i].address_line1 || !arr[i].address_line1.trim()) { toast.error("Address Line 1 is required"); return false; }
    arr[i] = { ...arr[i], _editing: false }; setForm({ ...form, shipping_addresses: arr }); return true;
  }
  function saveShipAndAdd(i) { if (saveShip(i)) addShip(); }

  async function verifyGst() {
    if (!form.gst_number) { toast.error("Enter a GST Number first"); return; }
    // Future hook for actual GST portal verification API. For now we mark verified locally.
    set("gst_verified", true);
    set("gst_verified_at", new Date().toISOString());
    toast.success("GST marked verified (portal integration coming soon)");
  }

  async function save(e) {
    e.preventDefault();
    if (!form.client_code || !form.name) { toast.error("Code and Name are required"); return; }
    if (!form.branch_id) { toast.error("Branch is mandatory"); return; }
    setSaving(true);
    try {
      // Strip UI-only flags before sending
      const payload = {
        ...form,
        pocs: (form.pocs || []).map(({ _editing, ...rest }) => rest),
        shipping_addresses: (form.shipping_addresses || []).map(({ _editing, ...rest }) => rest),
      };
      if (form.id) await api.put(`/clients/${form.id}`, payload);
      else await api.post("/clients", payload);
      toast.success(form.id ? "Updated" : "Created"); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]" data-testid="client-form-overlay">
      <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-[38px] bg-white z-10 shadow-sm">
        <h2 className="font-display text-2xl font-bold">{form.id ? "Edit" : "New"} Client</h2>
        <div className="flex items-center gap-3">
          <button data-testid="save-client-btn" form="client-form" type="submit" disabled={saving} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
            <Save size={14} /> {saving ? "Saving…" : "Save Client"}
          </button>
          <button onClick={onClose} className="p-2 hover:bg-slate-100"><X size={20} /></button>
        </div>
      </div>

      <form id="client-form" onSubmit={save} className="px-8 py-6 space-y-5 max-w-[1400px] mx-auto">
          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <Building2 size={14} className="text-slate-500" />
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Identity & Statutory</h3>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Branch *">
                <BranchSelect value={form.branch_id} onChange={(id, name) => setForm({ ...form, branch_id: id, branch_name: name })} testid="client-branch-select" />
              </Field>
              <Field label="Client Code *"><input data-testid="client-code" value={form.client_code} onChange={e => set("client_code", e.target.value)} className={inputCls} /></Field>
              <Field label="Name *"><input data-testid="client-name" value={form.name} onChange={e => set("name", e.target.value)} className={inputCls} /></Field>
              <Field label="Phone"><input value={form.phone || ""} onChange={e => set("phone", e.target.value)} className={inputCls} /></Field>
              <Field label="Email"><input type="email" value={form.email || ""} onChange={e => set("email", e.target.value)} className={inputCls} /></Field>
              <Field label="Primary Contact (legacy)"><input value={form.contact_person || ""} onChange={e => set("contact_person", e.target.value)} className={inputCls} placeholder="(See POCs below for the new way)" /></Field>
              <Field label="Opening Balance (₹)"><input type="number" step="0.01" value={form.opening_balance || 0} onChange={e => set("opening_balance", Number(e.target.value))} className={inputCls} /></Field>
              <Field label="Credit Days (payment period)"><input data-testid="client-credit-days" type="number" min="0" value={form.credit_days ?? ""} onChange={e => set("credit_days", e.target.value === "" ? null : Number(e.target.value))} placeholder="e.g. 30" className={inputCls} /></Field>
              <Field label="Status">
                <label className="flex items-center gap-2 text-sm pt-2" data-testid="client-active-toggle">
                  <input type="checkbox" checked={form.is_active !== false} onChange={e => set("is_active", e.target.checked)}
                         className="w-4 h-4 accent-[#F97316]" />
                  <span className={form.is_active !== false ? "text-emerald-700 font-bold" : "text-slate-500"}>
                    {form.is_active !== false ? "Active" : "Inactive (hidden from transaction dropdowns)"}
                  </span>
                </label>
              </Field>
              <div className="md:col-span-2">
                <Field label="GST Number">
                  <div className="flex gap-2">
                    <input data-testid="client-gst" value={form.gst_number || ""} onChange={e => set("gst_number", e.target.value.toUpperCase())} placeholder="07AAACR1234A1Z5" className={inputCls + " flex-1"} />
                    <button type="button" data-testid="verify-gst" onClick={verifyGst} className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 text-[11px] uppercase tracking-wider font-bold whitespace-nowrap flex items-center gap-1.5">
                      <CheckCircle2 size={12} /> Verify
                    </button>
                  </div>
                  {form.gst_verified && (
                    <div className="text-[10px] text-emerald-700 mt-1">Verified {form.gst_verified_at ? `· ${new Date(form.gst_verified_at).toLocaleDateString()}` : ""}</div>
                  )}
                </Field>
              </div>
              <Field label="PAN Number"><input data-testid="client-pan" value={form.pan || ""} onChange={e => set("pan", e.target.value.toUpperCase())} placeholder="AAACR1234A" className={inputCls} /></Field>
              <Field label="CIN (if Pvt/Ltd)"><input value={form.cin || ""} onChange={e => set("cin", e.target.value.toUpperCase())} className={inputCls} /></Field>
              <div className="md:col-span-3">
                <Field label="Registered Address"><textarea rows={2} value={form.address || ""} onChange={e => set("address", e.target.value)} className={inputCls} /></Field>
              </div>
            </div>
          </Card>

          {/* Jul-2026 — Estimate reminder CC escalation (client-wise) */}
          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <Mail size={14} className="text-slate-500" />
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Estimate Reminder CC Escalation</h3>
              <span className="text-[10px] text-slate-400">— optional · applies to this client's estimate reminder emails</span>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              <EmailListInput
                label="Reminder CC (all reminders)"
                testid="client-reminder-cc"
                value={form.reminder_cc}
                onChange={v => set("reminder_cc", v)}
                hint="Added to every reminder, along with the global Email Config CC."
              />
              <EmailListInput
                label="4th Reminder CC (escalation)"
                testid="client-reminder-cc-4th"
                value={form.reminder_cc_4th}
                onChange={v => set("reminder_cc_4th", v)}
                hint="Included from the 4th reminder (or once pending 4+ days) onwards, in addition to the above."
              />
              <EmailListInput
                label="7th Reminder CC (escalation)"
                testid="client-reminder-cc-7th"
                value={form.reminder_cc_7th}
                onChange={v => set("reminder_cc_7th", v)}
                hint="Included from the 7th reminder (or once pending 7+ days) onwards, in addition to all the above."
              />
            </div>
          </Card>

          {/* Billing Address */}
          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <MapPin size={14} className="text-slate-500" />
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Billing Address</h3>
            </div>
            <div className="p-5">
              <textarea data-testid="client-billing" rows={3} value={form.billing_address || ""} onChange={e => set("billing_address", e.target.value)} placeholder="Default billing address auto-fills onto every invoice / quote raised for this client" className={inputCls} />
            </div>
          </Card>

          {/* Shipping Addresses (multi) */}
          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-slate-500" />
                <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Shipping Addresses</h3>
                <span className="text-[10px] text-slate-400">— selectable in Quote &amp; Sales Invoice</span>
              </div>
              <button type="button" data-testid="add-ship-btn" onClick={addShip} className="text-[10px] font-bold uppercase tracking-wider text-[#F97316] hover:underline flex items-center gap-1"><Plus size={12} /> Add Shipping Address</button>
            </div>
            <div className="p-5 space-y-4">
              {(form.shipping_addresses || []).length === 0 && (
                <div className="text-center py-6 text-xs text-slate-500">No shipping addresses yet. Click <strong>Add Shipping Address</strong>.</div>
              )}
              {(form.shipping_addresses || []).map((s, i) => s._editing ? (
                <div key={i} className="border border-slate-200 p-4 bg-slate-50/30" data-testid={`ship-row-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <input value={s.label || ""} onChange={e => setShip(i, "label", e.target.value)} placeholder="Label (e.g. Warehouse A — Mumbai) *" className={`${inputCls} font-bold max-w-md`} />
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1.5 text-[11px]">
                        <input type="radio" name="default_ship" checked={!!s.is_default} onChange={() => makeDefault(i)} /> Default
                      </label>
                      <button type="button" onClick={() => delShip(i)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input data-testid={`ship-code-${i}`} value={s.code || ""} onChange={e => setShip(i, "code", e.target.value)} placeholder="Code (e.g. Store SAP code)" className={inputCls} />
                    <input data-testid={`ship-format-${i}`} value={s.format || ""} onChange={e => setShip(i, "format", e.target.value)} placeholder="Format (e.g. Smart Bazaar / Warehouse)" className={inputCls} />
                    <div />
                    <input value={s.address_line1} onChange={e => setShip(i, "address_line1", e.target.value)} placeholder="Address Line 1 *" className={inputCls} />
                    <input value={s.address_line2 || ""} onChange={e => setShip(i, "address_line2", e.target.value)} placeholder="Address Line 2" className={inputCls} />
                    <input value={s.city || ""} onChange={e => setShip(i, "city", e.target.value)} placeholder="City" className={inputCls} />
                    <input value={s.state || ""} onChange={e => setShip(i, "state", e.target.value)} placeholder="State" className={inputCls} />
                    <input value={s.pincode || ""} onChange={e => setShip(i, "pincode", e.target.value)} placeholder="PIN Code" className={inputCls} />
                    <div />
                    <input value={s.contact_person || ""} onChange={e => setShip(i, "contact_person", e.target.value)} placeholder="Site Contact Name" className={inputCls} />
                    <input value={s.contact_phone || ""} onChange={e => setShip(i, "contact_phone", e.target.value)} placeholder="Site Contact Phone" className={inputCls} />
                    <input data-testid={`ship-email-${i}`} type="email" value={s.email || ""} onChange={e => setShip(i, "email", e.target.value)} placeholder="Shipping Address Email (auto-added as TO on Estimate emails)" className={inputCls} />
                    <input value={s.gst_number || ""} onChange={e => setShip(i, "gst_number", e.target.value.toUpperCase())} placeholder="GST Number (same as Client by default)" className={inputCls + " tabular"} />
                    <input value={s.pan || ""} onChange={e => setShip(i, "pan", e.target.value.toUpperCase())} placeholder="PAN Number (same as Client by default)" className={inputCls + " tabular"} />
                    <button type="button" onClick={() => {
                      const arr = [...(form.shipping_addresses || [])];
                      arr[i] = { ...arr[i], gst_number: form.gst_number || "", pan: form.pan || "" };
                      setForm({ ...form, shipping_addresses: arr });
                    }} className="text-[10px] uppercase tracking-wider font-bold text-[#F97316] hover:underline self-center text-left">
                      Copy GST &amp; PAN from Client
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-200">
                    <button type="button" data-testid={`save-ship-${i}`} onClick={() => saveShip(i)} className="bg-slate-800 hover:bg-slate-900 text-white px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5"><Save size={12} /> Save</button>
                    <button type="button" data-testid={`save-add-ship-${i}`} onClick={() => saveShipAndAdd(i)} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5"><Plus size={12} /> Save &amp; Add Another</button>
                  </div>
                </div>
              ) : (
                <div key={i} className="border border-slate-200 px-4 py-3 bg-white flex items-center justify-between hover:bg-slate-50/60" data-testid={`ship-row-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm">{s.label || "(no label)"}</span>
                      {s.code && <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-700 px-1.5 py-0.5 font-bold tabular">{s.code}</span>}
                      {s.format && <span className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-800 px-1.5 py-0.5 font-bold">{s.format}</span>}
                      {s.is_default && <span className="text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 font-bold">Default</span>}
                    </div>
                    <div className="text-xs text-slate-600 truncate mt-0.5">
                      {[s.address_line1, s.address_line2, s.city, s.state, s.pincode].filter(Boolean).join(", ")}
                      {(s.contact_person || s.contact_phone) && <span className="text-slate-400"> · {s.contact_person} {s.contact_phone && `(${s.contact_phone})`}</span>}
                      {s.email && <span className="text-[#F97316]"> · ✉ {s.email}</span>}
                    </div>
                    {(s.gst_number || s.pan) && (
                      <div className="text-[11px] text-slate-500 tabular mt-0.5">
                        {s.gst_number && <span>GST {s.gst_number}</span>}
                        {s.gst_number && s.pan && <span className="mx-2">·</span>}
                        {s.pan && <span>PAN {s.pan}</span>}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <label className="flex items-center gap-1.5 text-[11px]">
                      <input type="radio" name="default_ship" checked={!!s.is_default} onChange={() => makeDefault(i)} /> Default
                    </label>
                    <button type="button" onClick={() => editShip(i)} className="text-slate-500 hover:text-[#F97316] p-1"><Pencil size={14} /></button>
                    <button type="button" onClick={() => delShip(i)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* POCs (multi) */}
          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserCheck size={14} className="text-slate-500" />
                <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Points of Contact (POCs)</h3>
              </div>
              <button type="button" data-testid="add-poc-btn" onClick={addPoc} className="text-[10px] font-bold uppercase tracking-wider text-[#F97316] hover:underline flex items-center gap-1"><Plus size={12} /> Add POC</button>
            </div>
            <div className="p-5 space-y-3">
              {(form.pocs || []).length === 0 && (
                <div className="text-center py-6 text-xs text-slate-500">No POCs yet. POCs are pickable inside documents (location to be defined).</div>
              )}
              {(form.pocs || []).map((p, i) => p._editing ? (
                <div key={i} className="border border-slate-200 p-4 bg-slate-50/30" data-testid={`poc-row-${i}`}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
                    <input value={p.name || ""} onChange={e => setPoc(i, "name", e.target.value)} placeholder="Name *" className={`${inputCls} md:col-span-3`} />
                    <input value={p.designation || ""} onChange={e => setPoc(i, "designation", e.target.value)} placeholder="Designation" className={`${inputCls} md:col-span-2`} />
                    <input value={p.phone || ""} onChange={e => setPoc(i, "phone", e.target.value)} placeholder="Phone" className={`${inputCls} md:col-span-2`} />
                    <input value={p.email || ""} onChange={e => setPoc(i, "email", e.target.value)} placeholder="Email" className={`${inputCls} md:col-span-3`} />
                    <input value={p.notes || ""} onChange={e => setPoc(i, "notes", e.target.value)} placeholder="Notes" className={`${inputCls} md:col-span-2`} />
                  </div>
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200">
                    <button type="button" data-testid={`save-poc-${i}`} onClick={() => savePoc(i)} className="bg-slate-800 hover:bg-slate-900 text-white px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5"><Save size={12} /> Save</button>
                    <button type="button" data-testid={`save-add-poc-${i}`} onClick={() => savePocAndAdd(i)} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5"><Plus size={12} /> Save &amp; Add Another</button>
                    <button type="button" onClick={() => delPoc(i)} className="ml-auto text-red-500 hover:text-red-700 p-1"><Trash2 size={14} /></button>
                  </div>
                </div>
              ) : (
                <div key={i} className="border border-slate-200 px-4 py-2.5 bg-white flex items-center justify-between hover:bg-slate-50/60" data-testid={`poc-row-${i}`}>
                  <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-12 gap-2 text-xs">
                    <div className="md:col-span-3 font-bold text-sm truncate">{p.name || "(no name)"}</div>
                    <div className="md:col-span-2 text-slate-600 truncate">{p.designation || "—"}</div>
                    <div className="md:col-span-2 text-slate-600 truncate">{p.phone || "—"}</div>
                    <div className="md:col-span-3 text-slate-600 truncate">{p.email || "—"}</div>
                    <div className="md:col-span-2 text-slate-400 truncate">{p.notes || ""}</div>
                  </div>
                  <div className="flex items-center gap-1 ml-3">
                    <button type="button" onClick={() => editPoc(i)} className="text-slate-500 hover:text-[#F97316] p-1"><Pencil size={14} /></button>
                    <button type="button" onClick={() => delPoc(i)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </form>
    </div>
  );
}


function Row({ label, value, mono = false }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-1.5 border-b border-slate-50 last:border-b-0">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</div>
      <div className={`col-span-2 text-sm text-slate-900 ${mono ? "tabular" : ""}`}>{value || <span className="text-slate-400">—</span>}</div>
    </div>
  );
}

function SectionTitle({ icon: Icon, children, right }) {
  return (
    <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon size={14} className="text-slate-500" />
        <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">{children}</h3>
      </div>
      {right}
    </div>
  );
}

function ClientView({ record, onClose, onEdit }) {
  const c = record || {};
  const ship = c.shipping_addresses || [];
  const pocs = c.pocs || [];
  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]" data-testid="client-view-overlay">
        <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-[38px] bg-white z-10 shadow-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-bold">Client Details</div>
            <h2 className="font-display text-2xl font-bold mt-0.5">{c.name}</h2>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="view-edit-btn" onClick={onEdit} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
              <Pencil size={14} /> Edit
            </button>
            <button data-testid="view-close-btn" onClick={onClose} className="p-2 hover:bg-slate-100"><X size={20} /></button>
          </div>
        </div>

        <div className="px-8 py-6 space-y-5 max-w-[1400px] mx-auto">
          {/* Summary band */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="border border-slate-200 p-4">
              <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-bold">Client Code</div>
              <div className="text-lg font-bold mt-1">{c.client_code || "—"}</div>
            </div>
            <div className="border border-slate-200 p-4">
              <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-bold">Opening Balance</div>
              <div className="text-lg font-bold mt-1 tabular">{formatINR(c.opening_balance || 0)}</div>
            </div>
            <div className="border border-slate-200 p-4">
              <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-bold">Points of Contact</div>
              <div className="text-lg font-bold mt-1">{pocs.length}</div>
            </div>
            <div className="border border-slate-200 p-4">
              <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-bold">Shipping Locations</div>
              <div className="text-lg font-bold mt-1">{ship.length}</div>
            </div>
          </div>

          {/* Identity & Statutory */}
          <Card>
            <SectionTitle icon={Building2}>Identity &amp; Statutory</SectionTitle>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-x-10">
              <Row label="Name" value={c.name} />
              <Row label="Phone" value={c.phone} />
              <Row label="Email" value={c.email} />
              <Row label="Primary Contact (legacy)" value={c.contact_person} />
              <Row label="GST Number" value={c.gst_number && (
                <span className="inline-flex items-center gap-1.5">
                  <span className="tabular">{c.gst_number}</span>
                  {c.gst_verified && <span className="text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 font-bold inline-flex items-center gap-1"><CheckCircle2 size={10}/> Verified</span>}
                </span>
              )} />
              <Row label="PAN" value={c.pan} mono />
              <Row label="CIN" value={c.cin} mono />
              <Row label="GST Verified At" value={c.gst_verified_at ? new Date(c.gst_verified_at).toLocaleString() : ""} />
            </div>
            {c.address && (
              <div className="px-5 pb-5">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-1">Registered Address</div>
                <div className="text-sm text-slate-800 whitespace-pre-line">{c.address}</div>
              </div>
            )}
          </Card>

          {/* Billing */}
          <Card>
            <SectionTitle icon={MapPin}>Billing Address</SectionTitle>
            <div className="p-5">
              {c.billing_address ? (
                <div className="text-sm text-slate-800 whitespace-pre-line">{c.billing_address}</div>
              ) : (
                <div className="text-xs text-slate-400">No billing address recorded.</div>
              )}
            </div>
          </Card>

          {/* Shipping list */}
          <Card>
            <SectionTitle icon={MapPin}>Shipping Addresses ({ship.length})</SectionTitle>
            <div className="p-5 space-y-3">
              {ship.length === 0 && <div className="text-xs text-slate-400">No shipping addresses on file.</div>}
              {ship.map((s, i) => (
                <div key={i} className="border border-slate-200 p-4 bg-slate-50/30" data-testid={`view-ship-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm">{s.label || "(no label)"}</span>
                      {s.code && <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-700 px-1.5 py-0.5 font-bold tabular">{s.code}</span>}
                      {s.format && <span className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-800 px-1.5 py-0.5 font-bold">{s.format}</span>}
                      {s.is_default && <span className="text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 font-bold">Default</span>}
                    </div>
                  </div>
                  <div className="text-sm text-slate-800">
                    {[s.address_line1, s.address_line2, s.city, s.state, s.pincode].filter(Boolean).join(", ")}
                  </div>
                  {(s.contact_person || s.contact_phone) && (
                    <div className="text-xs text-slate-600 mt-1.5 flex items-center gap-4">
                      {s.contact_person && <span className="inline-flex items-center gap-1"><UserCheck size={11} /> {s.contact_person}</span>}
                      {s.contact_phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {s.contact_phone}</span>}
                    </div>
                  )}
                  {(s.gst_number || s.pan) && (
                    <div className="text-xs text-slate-600 mt-1.5 flex items-center gap-4 tabular">
                      {s.gst_number && <span><span className="font-bold text-slate-500">GST:</span> {s.gst_number}</span>}
                      {s.pan && <span><span className="font-bold text-slate-500">PAN:</span> {s.pan}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* POCs */}
          <Card>
            <SectionTitle icon={UserCheck}>Points of Contact ({pocs.length})</SectionTitle>
            <div className="p-5">
              {pocs.length === 0 ? (
                <div className="text-xs text-slate-400">No POCs on file.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b-2 border-slate-200">
                    <tr>{["Name", "Designation", "Phone", "Email", "Notes"].map(h => (
                      <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-3">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {pocs.map((p, i) => (
                      <tr key={i} className="border-b border-slate-100" data-testid={`view-poc-${i}`}>
                        <td className="py-2 px-3 font-bold">{p.name || "—"}</td>
                        <td className="py-2 px-3 text-slate-700">{p.designation || "—"}</td>
                        <td className="py-2 px-3 text-slate-700">{p.phone ? <span className="inline-flex items-center gap-1"><Phone size={11}/>{p.phone}</span> : "—"}</td>
                        <td className="py-2 px-3 text-slate-700">{p.email ? <span className="inline-flex items-center gap-1"><Mail size={11}/>{p.email}</span> : "—"}</td>
                        <td className="py-2 px-3 text-slate-500 text-xs">{p.notes || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
        </div>
    </div>
  );
}

// Jul-2026 — Shipping Address Email bulk update (download pre-filled Excel,
// fill the "Shipping Email" column, upload back).
function ShipEmailBulkButtons({ onUploaded }) {
  const [busy, setBusy] = useState(false);

  async function downloadTemplate() {
    try {
      const res = await api.get("/clients/shipping-email-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "client_shipping_email_template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Template download failed"); }
  }

  async function upload(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/clients/shipping-email-bulk-update", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      const errN = (data.errors || []).length;
      if (data.updated > 0) toast.success(`${data.updated} shipping email(s) updated · ${data.skipped} skipped${errN ? ` · ${errN} error(s)` : ""}`);
      else toast.warning(`No emails updated — ${data.skipped} skipped${errN ? `, first error: ${data.errors[0]?.error}` : ""}`);
      if (errN) console.warn("Ship-email bulk update errors:", data.errors);
      onUploaded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk update failed");
    } finally { setBusy(false); }
  }

  return (
    <>
      <button type="button" data-testid="ship-email-template-btn" onClick={downloadTemplate}
        className="border border-slate-300 hover:border-[#F97316] text-slate-700 px-3 py-2.5 text-xs uppercase tracking-wider font-bold">
        Ship-Email Template
      </button>
      <label data-testid="ship-email-upload-btn"
        className={`border border-slate-300 hover:border-[#F97316] text-slate-700 px-3 py-2.5 text-xs uppercase tracking-wider font-bold cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}>
        {busy ? "Updating…" : "Bulk Update Ship Emails"}
        <input type="file" accept=".xlsx" className="hidden" onChange={upload} />
      </label>
    </>
  );
}
